import { useMemo } from 'react';
import { useQuery, keepPreviousData } from '@tanstack/react-query';
import {
  TasksService,
  type TaskListQuery,
  type Task,
  TaskAssigneeType,
} from 'src/api/generated';
import type { TaskFiltersState } from '../../types/agenda.types';

/**
 * Parameters for the agenda tasks calendar hook
 */
interface UseAgendaTasksCalendarParams {
  /** Task filters to apply when fetching tasks */
  filters?: TaskFiltersState;
  /** Whether the query should be enabled. Defaults to true */
  enabled?: boolean;
  /** Calendar-specific: ISO date string for start of visible calendar range */
  startDate?: string;
  /** Calendar-specific: ISO date string for end of visible calendar range */
  endDate?: string;
}

interface UseAgendaTasksCalendarResult {
  tasks: Task[];
  isLoading: boolean;
  isFetching: boolean;
  error: Error | null;
  refetch: () => Promise<unknown>;
  tasksOverflowByDate: Record<string, number>;
}

/** Maximum tasks to show per day in calendar */
const MAX_TASKS_PER_DAY = 3;

export const useAgendaTasksCalendar = (
  params: UseAgendaTasksCalendarParams = {}
): UseAgendaTasksCalendarResult => {
  const { filters = {}, enabled = true, startDate, endDate } = params;

  const fetchTasks = async () => {
    const fetchParams: TaskListQuery = {
      limit: 1000,
    };

    if (startDate) fetchParams.dueAfter = startDate;
    if (endDate) fetchParams.dueBefore = endDate;

    if (filters.status) fetchParams.status = filters.status;
    if (filters.assignedToType) {
      fetchParams.assignedToType =
        filters.assignedToType === TaskAssigneeType.USER
          ? TaskAssigneeType.USER
          : TaskAssigneeType.AI_EMPLOYEE;
    }
    if (filters.assignedToId) fetchParams.assignedToId = filters.assignedToId;
    if (filters.dueBefore) fetchParams.dueBefore = filters.dueBefore;
    if (filters.dueAfter) fetchParams.dueAfter = filters.dueAfter;

    if (filters.conversationId && typeof filters.conversationId === 'number') {
      fetchParams.conversationId = filters.conversationId;
    }

    return await TasksService.getTasks(
      fetchParams.limit,
      undefined,
      fetchParams.dueBefore,
      fetchParams.dueAfter,
      fetchParams.conversationId,
      fetchParams.assignedToType,
      fetchParams.assignedToId,
      fetchParams.status
    );
  };

  const { data, error, isLoading, isFetching, refetch } = useQuery({
    queryKey: ['agenda-tasks-calendar', filters, startDate, endDate],
    queryFn: fetchTasks,
    enabled: enabled && !!startDate && !!endDate,
    placeholderData: keepPreviousData,
    staleTime: 30000,
  });

  const { tasks, tasksOverflowByDate } = useMemo(() => {
    if (!data?.items) return { tasks: [], tasksOverflowByDate: {} };

    let allTasks = data.items;

    if (filters.conversationId === 'none') {
      allTasks = allTasks.filter((task) => !task.context.conversationId);
    }

    const tasksByDate = new Map<string, Task[]>();
    const overflow: Record<string, number> = {};

    allTasks.forEach((task) => {
      if (!task.dueDate) return;

      const dateKey = task.dueDate.split('T')[0];
      const tasksForDate = tasksByDate.get(dateKey) ?? [];
      tasksForDate.push(task);
      tasksByDate.set(dateKey, tasksForDate);
    });

    tasksByDate.forEach((tasksForDate, date) => {
      if (tasksForDate.length > MAX_TASKS_PER_DAY) {
        overflow[date] = tasksForDate.length - MAX_TASKS_PER_DAY;
      }
    });

    return {
      tasks: allTasks,
      tasksOverflowByDate: overflow,
    };
  }, [data?.items, filters.conversationId]);

  return {
    tasks,
    isLoading,
    isFetching,
    error,
    refetch,
    tasksOverflowByDate,
  };
};
